export default {
  data() {
    return {
      showTopScroll: false,
      topScrollWidth: 0,
      isSyncingScroll: false,
      intersectionObserver: undefined,
      bottomSentinelEl: undefined,
      isBottomInViewport: false,
    };
  },

  mounted() {
    if (this.enableTopScroll) {
      this.$nextTick(() => {
        this.updateTopScrollMetrics();
        this.attachScrollSync();
        this.setupIntersectionObserver();
      });
      window.addEventListener('resize', this.handleWindowResize, { passive: true });
    }
  },

  updated() {
    this.$nextTick(() => {
      this.updateTopScrollMetrics();
      this.setupIntersectionObserver();
    });
  },

  beforeDestroy() {
    const main = this.$refs.mainScroll;
    const top = this.$refs.topScroll;
    if (top) top.removeEventListener('scroll', this.onTopScroll);
    if (main) main.removeEventListener('scroll', this.onMainScroll);
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }
    if (this.bottomSentinelEl && this.bottomSentinelEl.parentNode) {
      this.bottomSentinelEl.parentNode.removeChild(this.bottomSentinelEl);
      this.bottomSentinelEl = undefined;
    }
  },

  methods: {
    setupIntersectionObserver() {
      if (!this.enableTopScroll) return;
      const main = this.$refs.mainScroll;
      if (!main || typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

      // Ensure sentinel exists and is appended to main
      if (!this.bottomSentinelEl || this.bottomSentinelEl.parentNode !== main) {
        if (this.bottomSentinelEl && this.bottomSentinelEl.parentNode) {
          this.bottomSentinelEl.parentNode.removeChild(this.bottomSentinelEl);
        }
        const sentinel = document.createElement('div');
        sentinel.className = 'k-bottom-scroll-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.width = '1px';
        sentinel.style.height = '1px';
        sentinel.style.opacity = '0';
        sentinel.style.pointerEvents = 'none';
        main.appendChild(sentinel);
        this.bottomSentinelEl = sentinel;
      }

      // Ensure observer exists and is watching the sentinel
      if (!this.intersectionObserver) {
        this.intersectionObserver = new window.IntersectionObserver(this.onIntersectionChange, {
          root: null,
          threshold: 0,
        });
      } else {
        // Reset observation in case the sentinel changed
        this.intersectionObserver.disconnect();
      }
      this.intersectionObserver.observe(this.bottomSentinelEl);
    },

    onIntersectionChange(entries) {
      // If any entry is intersecting, the bottom is in viewport
      this.isBottomInViewport = entries.some(e => e.isIntersecting);
      this.updateTopScrollVisibility();
    },

    attachScrollSync() {
      if (!this.enableTopScroll) return;
      const main = this.$refs.mainScroll;
      const top = this.$refs.topScroll;
      if (!main || !top) return;
      top.addEventListener('scroll', this.onTopScroll, { passive: true });
      main.addEventListener('scroll', this.onMainScroll, { passive: true });
      top.scrollLeft = main.scrollLeft;
    },

    onTopScroll() {
      if (!this.enableTopScroll || this.isSyncingScroll) return;
      this.isSyncingScroll = true;
      const main = this.$refs.mainScroll;
      const top = this.$refs.topScroll;
      if (main && top) main.scrollLeft = top.scrollLeft;
      this.isSyncingScroll = false;
    },

    onMainScroll() {
      if (!this.enableTopScroll || this.isSyncingScroll) return;
      this.isSyncingScroll = true;
      const main = this.$refs.mainScroll;
      const top = this.$refs.topScroll;
      if (main && top) top.scrollLeft = main.scrollLeft;
      this.isSyncingScroll = false;
      // No need to compute viewport metrics here; observer updates visibility
    },

    handleWindowResize() {
      if (this.enableTopScroll) this.updateTopScrollMetrics();
    },

    updateTopScrollMetrics() {
      if (!this.enableTopScroll) return;
      this.updateTopScrollWidth();
      this.updateTopScrollVisibility();
    },

    updateTopScrollWidth() {
      if (!this.enableTopScroll) return;
      const main = this.$refs.mainScroll;
      if (!main) return;
      this.topScrollWidth = main.scrollWidth || 0;
    },

    updateTopScrollVisibility() {
      if (!this.enableTopScroll) return;
      const main = this.$refs.mainScroll;
      if (!main) {
        this.showTopScroll = false;
        return;
      }
      const hasHorizontalOverflow = main.scrollWidth > main.clientWidth + 1;
      // IntersectionObserver sets isBottomInViewport
      this.showTopScroll = hasHorizontalOverflow && !this.isBottomInViewport;
    },
  },
};
